import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { IconButton, Tooltip, Box } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DataTable, { ColumnConfig } from '../../Shared/Components/DataTable';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import InfoCard from '../../Shared/Components/InfoCard/InfoCard';
import InfoIcon from '@mui/icons-material/Info';
import { useCreateRedirect } from '../../Shared/Hooks/useCreateRedirect';
import { useDeleteSelectedItems } from '../../Shared/Hooks/useDeleteSelectedItems';
import { usePagination } from '../../Shared/Hooks/usePagination';
import { useSelection } from '../../Shared/Hooks/useSelection';
import { InvoicesRoutes } from '../../../Router/path';
import { useDeleteInvoiceMutation } from '../Service/invoicesApi';
import { useGetInvoicesSearchV2Query } from '../Service/extendedInvoicesApi';
import { InvoiceType, InvoiceStatus } from '../Types/invoicesTypes';
import type { InvoiceSearchV2ListItem, InvoiceSearchV2Filters } from '../Types/invoiceSearchV2Types';
import { defaultInvoiceSearchV2Filters } from '../Types/invoiceSearchV2Types';
import { mapInvoiceSearchFiltersToRequest } from '../Service/mapInvoiceSearchFiltersToRequest';
import InvoicesSearchForm from '../Components/InvoicesSearchForm';
import { InvoicePdfPreviewModal } from '../Components';
import { extractInvoiceSearchError } from '../Types/invoiceSearchErrorUtils';
import { formatAmount } from '../utils/vatCalculations';

const UTC_LOCALE_DATE_FORMATTER = new Intl.DateTimeFormat(undefined, { timeZone: 'UTC' });

const formatLocaleDate = (value: string): string => {
  const datePart = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return value;
  }
  const [year, month, day] = datePart.split('-').map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return value;
  }
  const utcDate = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(utcDate.getTime())) {
    return value;
  }
  if (
    utcDate.getUTCFullYear() !== year ||
    utcDate.getUTCMonth() !== month - 1 ||
    utcDate.getUTCDate() !== day
  ) {
    return value;
  }
  return UTC_LOCALE_DATE_FORMATTER.format(utcDate);
};

const formatListAmount = (row: InvoiceSearchV2ListItem): string => {
  if (row.currency) {
    return formatAmount(row.totalAmount, row.currency);
  }
  return row.totalAmount.toFixed(2);
};

const Invoices = () => {
  const [filters, setFilters] = useState<InvoiceSearchV2Filters>(defaultInvoiceSearchV2Filters);
  const [previewInvoiceId, setPreviewInvoiceId] = useState<number | null>(null);
  const [previewInvoiceNumber, setPreviewInvoiceNumber] = useState<string | undefined>(undefined);
  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { selected: selectedIds, setSelected, resetSelection } = useSelection<number>();
  const navigate = useNavigate();

  const queryArgs = mapInvoiceSearchFiltersToRequest(filters, { page, pageSize: rowsPerPage });
  const { data, isLoading, isError, error } = useGetInvoicesSearchV2Query(queryArgs);

  const [deleteInvoice] = useDeleteInvoiceMutation();
  const handleCreate = useCreateRedirect(InvoicesRoutes.create);
  const handleDeleteSelected = useDeleteSelectedItems<number>(
    selectedIds,
    (id) => deleteInvoice(id).unwrap(),
    resetSelection,
    {
      errorPrefix: 'Nie udało się usunąć faktury.',
    }
  );

  const { message: errorMessage } = isError ? extractInvoiceSearchError(error) : { message: '' };

  const columns: ColumnConfig<InvoiceSearchV2ListItem>[] = [
    { key: 'invoiceNumber', label: 'Numer' },
    { key: 'issueDate', label: 'Data wystawienia', render: (row) => formatLocaleDate(row.issueDate) },
    { key: 'totalAmount', label: 'Kwota', render: (row) => formatListAmount(row) },
    { key: 'buyer', label: 'Nabywca' },
    { key: 'recipientName', label: 'Odbiorca', render: (row) => row.recipientName || '—' },
    { key: 'applicantFullName', label: 'Klient', render: (row) => row.applicantFullName || '—' },
    { key: 'offerName', label: 'Oferta', render: (row) => row.offerName || '—' },
  ];

  return (
    <>
      <InfoCard
        icon={<InfoIcon fontSize="large" color="primary" />}
        title="Faktury"
        subheader="Zarządzaj fakturami w systemie"
        infoText="Na tej stronie zarządzasz wszystkimi wystawionymi fakturami. Możesz filtrować, przeglądać i edytować dokumenty."
        hintText="Skorzystaj z przycisków, aby dodać nową fakturę lub usunąć wybrane pozycje."
        onCreate={handleCreate}
        onDeleteSelected={handleDeleteSelected}
      >
        <InvoicesSearchForm
          onSearch={(vals) => {
            setFilters(vals);
            setPage(0);
          }}
        />
      </InfoCard>

      <CustomCard>
        <DataTable
          columns={columns}
          data={data?.items || []}
          isLoading={isLoading}
          isError={isError}
          errorMessage={errorMessage}
          getRowId={(row) => row.id}
          onEdit={(id) => {
            const item = data?.items.find((x) => x.id === id);
            if (item) {
              navigate(InvoicesRoutes.update.replace(':invoiceId', String(item.id)));
            }
          }}
          renderCustomActions={(row) => (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Podgląd">
                <IconButton
                  onClick={() => {
                    setPreviewInvoiceId(row.id);
                    setPreviewInvoiceNumber(row.invoiceNumber);
                  }}
                  size="small"
                  color="primary"
                >
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              {row.type !== InvoiceType.Correction &&
                row.status !== InvoiceStatus.Draft &&
                row.status !== InvoiceStatus.Cancelled && (
                  <Tooltip title="Utwórz fakturę korygującą">
                    <IconButton
                      onClick={() => navigate(`/invoices/correct/${row.id}`)}
                      size="small"
                      color="secondary"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                )}
            </Box>
          )}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.totalCount || 0}
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newSize) => setRowsPerPage(newSize)}
          selected={selectedIds}
          onSelectionChange={setSelected}
        />
      </CustomCard>

      <InvoicePdfPreviewModal
        open={!!previewInvoiceId}
        onClose={() => {
          setPreviewInvoiceId(null);
          setPreviewInvoiceNumber(undefined);
        }}
        invoiceId={previewInvoiceId}
        invoiceNumber={previewInvoiceNumber}
      />

      <Outlet />
    </>
  );
};

export default Invoices;
